import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './RifleScore.css';
import { Award, Crosshair, Eye, Plus, Trash2, Zap } from 'lucide-react';
import { eventsService, rifleService } from '../../../../../services';
import CreateEvent from './components/CreateEvent';
import EditEvent from './components/EditEvent';
import EventCard from './components/EventCard';
import EditShooters from './components/EditShooters';
import ScoreSheetManagement from './components/ScoreSheetManagement';

const zeroDefaults = () => ({
    rifleName: '', scopeName: '', distance: '100', distanceUnit: 'metres',
    clicksElevation: '', clicksWindage: '', elevationDirection: 'Up', windageDirection: 'Right',
    clickValue: '', clickUnit: 'MOA', ammunition: '', muzzleVelocity: '', ballisticCoefficient: '',
    ballisticNotes: '', zeroConfirmed: false, confirmationShots: '', groupSize: '', groupSizeUnit: 'mm',
    verticalOffset: '', horizontalOffset: '', offsetUnit: 'mm', confirmationNotes: '',
    zeroDate: new Date().toISOString().slice(0, 10), conditions: '', notes: '', isBaseZero: true
});

const trainingDefaults = () => ({
    sessionType: 'LiveFire', discipline: 'TargetRifle', focus: '',
    sessionDate: new Date().toISOString().slice(0, 10), durationMinutes: '60',
    roundCount: '', repCount: '', drills: '', goals: '', observations: '',
    notes: '', rating: '', perceivedEffort: '', location: ''
});

const RifleScore = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activityMessage, setActivityMessage] = useState(null);
    const [activeTab, setActiveTab] = useState('scoresheets');
    const [scoreSheets, setScoreSheets] = useState([]);
    const [rifleEvents, setRifleEvents] = useState([]);
    const [zeros, setZeros] = useState([]);
    const [trainingSessions, setTrainingSessions] = useState([]);
    const [trainingStats, setTrainingStats] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [showCreateZero, setShowCreateZero] = useState(false);
    const [showCreateTraining, setShowCreateTraining] = useState(false);
    const [zeroForm, setZeroForm] = useState(zeroDefaults);
    const [trainingForm, setTrainingForm] = useState(trainingDefaults);
    const [submittingKey, setSubmittingKey] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editEventModalOpen, setEditEventModalOpen] = useState(false);
    const [editShootersModalOpen, setEditShootersModalOpen] = useState(false);
    const [scoreSheetManagementOpen, setScoreSheetManagementOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editEventInitialView, setEditEventInitialView] = useState('details');
    const [shootersRefreshKey, setShootersRefreshKey] = useState(0);

    const flash = useCallback((message) => {
        setActivityMessage(message);
        setTimeout(() => setActivityMessage(null), 2500);
    }, []);

    const fetchRifleScoreSheets = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await rifleService.getScoreSheets();
            setScoreSheets(data);
        } catch (err) {
            console.error('Error fetching score sheets:', err);
            setError('Failed to load rifle score sheets.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchRifleEvents = useCallback(async () => {
        try {
            const data = await eventsService.getEvents('Rifle');
            setRifleEvents(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching rifle events:', err);
        }
    }, []);

    const fetchZeros = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await rifleService.getZeros();
            setZeros(data);
        } catch (err) {
            console.error('Error fetching zeros:', err);
            setError('Failed to load zero records.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchTrainingSessions = useCallback(async () => {
        try {
            setIsLoading(true);
            const [sessionsData, statsData] = await Promise.all([
                rifleService.getTrainingSessions(),
                rifleService.getTrainingStats()
            ]);
            setTrainingSessions(sessionsData);
            setTrainingStats(statsData);
        } catch (err) {
            console.error('Error fetching training sessions:', err);
            setError('Failed to load training sessions.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        setError(null);
        setFilterText('');
        fetchRifleEvents();
        if (activeTab === 'scoresheets') fetchRifleScoreSheets();
        else if (activeTab === 'zeros') fetchZeros();
        else fetchTrainingSessions();
    }, [activeTab, fetchRifleEvents, fetchRifleScoreSheets, fetchTrainingSessions, fetchZeros]);

    const scoreSummary = useMemo(() => {
        const totalPossible = scoreSheets.reduce((total, sheet) => total + (sheet.possibleScore || 0), 0);
        const totalScored = scoreSheets.reduce((total, sheet) => total + (sheet.totalScore || 0), 0);
        return {
            totalSheets: scoreSheets.length,
            totalShooters: scoreSheets.reduce((total, sheet) => total + (sheet.shooterCount || 0), 0),
            averageCompletion: totalPossible > 0 ? Math.round((totalScored / totalPossible) * 100) : 0,
            longestDistance: scoreSheets.reduce((max, sheet) => Math.max(max, sheet.distance || 0), 0)
        };
    }, [scoreSheets]);

    const zeroSummary = useMemo(() => ({
        totalZeros: zeros.length,
        baseZeros: zeros.filter((zero) => zero.isBaseZero).length,
        confirmedZeros: zeros.filter((zero) => zero.zeroConfirmed).length,
        uniqueRifles: new Set(zeros.map((zero) => zero.rifleName || zero.equipmentName).filter(Boolean)).size,
        maxDistance: zeros.reduce((max, zero) => Math.max(max, zero.distance || 0), 0)
    }), [zeros]);

    const filteredScoreSheets = useMemo(() => {
        const term = filterText.trim().toLowerCase();
        if (!term) return scoreSheets;
        return scoreSheets.filter((sheet) =>
            [sheet.eventName, sheet.disciplineType, sheet.variant, sheet.position, sheet.rangeName, `${sheet.distance || ''}`]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(term))
        );
    }, [filterText, scoreSheets]);

    const filteredZeros = useMemo(() => {
        const term = filterText.trim().toLowerCase();
        if (!term) return zeros;
        return zeros.filter((zero) =>
            [zero.rifleName, zero.equipmentName, zero.scopeName, zero.ammunition, `${zero.distance || ''}`]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(term))
        );
    }, [filterText, zeros]);

    const filteredTrainingSessions = useMemo(() => {
        const term = filterText.trim().toLowerCase();
        if (!term) return trainingSessions;
        return trainingSessions.filter((session) =>
            [session.sessionType, session.discipline, session.focus, session.location, session.notes]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(term))
        );
    }, [filterText, trainingSessions]);

    const removeItem = async (url, successMessage, onSuccess) => {
        await url();
        onSuccess();
        flash(successMessage);
    };

    const handleDeleteScoreSheet = async (id) => {
        if (!window.confirm('Delete this score sheet?')) return;
        try {
            await removeItem(() => rifleService.deleteScoreSheet(id), 'Score sheet deleted', () => {
                setScoreSheets((current) => current.filter((sheet) => sheet.rifleScoreSheetId !== id));
            });
        } catch (err) {
            console.error('Error deleting score sheet:', err);
            setError('Failed to delete score sheet.');
        }
    };

    const handleDeleteZero = async (id) => {
        if (!window.confirm('Delete this zero record?')) return;
        try {
            await removeItem(() => rifleService.deleteZero(id), 'Zero record deleted', () => {
                setZeros((current) => current.filter((zero) => zero.rifleZeroId !== id));
            });
        } catch (err) {
            console.error('Error deleting zero:', err);
            setError('Failed to delete zero record.');
        }
    };

    const handleDeleteTraining = async (id) => {
        if (!window.confirm('Delete this training session?')) return;
        try {
            await removeItem(() => rifleService.deleteTrainingSession(id), 'Training session deleted', fetchTrainingSessions);
        } catch (err) {
            console.error('Error deleting training session:', err);
            setError('Failed to delete training session.');
        }
    };

    const handleCreateZero = async () => {
        try {
            setSubmittingKey('zero');
            setError(null);
            const createdZero = await rifleService.createZero({
                    rifleName: zeroForm.rifleName || null,
                    scopeName: zeroForm.scopeName || null,
                    distance: zeroForm.distance === '' ? 0 : Number(zeroForm.distance),
                    distanceUnit: zeroForm.distanceUnit || 'metres',
                    clicksElevation: zeroForm.clicksElevation === '' ? null : Number(zeroForm.clicksElevation),
                    clicksWindage: zeroForm.clicksWindage === '' ? null : Number(zeroForm.clicksWindage),
                    elevationDirection: zeroForm.elevationDirection || null,
                    windageDirection: zeroForm.windageDirection || null,
                    clickValue: zeroForm.clickValue === '' ? null : Number(zeroForm.clickValue),
                    clickUnit: zeroForm.clickUnit || null,
                    ammunition: zeroForm.ammunition || null,
                    muzzleVelocity: zeroForm.muzzleVelocity === '' ? null : Number(zeroForm.muzzleVelocity),
                    ballisticCoefficient: zeroForm.ballisticCoefficient === '' ? null : Number(zeroForm.ballisticCoefficient),
                    ballisticNotes: zeroForm.ballisticNotes || null,
                    zeroConfirmed: zeroForm.zeroConfirmed,
                    confirmationShots: zeroForm.confirmationShots === '' ? null : Number(zeroForm.confirmationShots),
                    groupSize: zeroForm.groupSize === '' ? null : Number(zeroForm.groupSize),
                    groupSizeUnit: zeroForm.groupSizeUnit || null,
                    verticalOffset: zeroForm.verticalOffset === '' ? null : Number(zeroForm.verticalOffset),
                    horizontalOffset: zeroForm.horizontalOffset === '' ? null : Number(zeroForm.horizontalOffset),
                    offsetUnit: zeroForm.offsetUnit || null,
                    confirmationNotes: zeroForm.confirmationNotes || null,
                    zeroDate: new Date(zeroForm.zeroDate).toISOString(),
                    conditions: zeroForm.conditions || null,
                    notes: zeroForm.notes || null,
                    isBaseZero: zeroForm.isBaseZero
            });
            setZeros((current) => [createdZero, ...current]);
            setZeroForm(zeroDefaults());
            setShowCreateZero(false);
            flash('Zero record created');
        } catch (err) {
            console.error('Error creating zero:', err);
            setError('Failed to create zero record.');
        } finally {
            setSubmittingKey(null);
        }
    };

    const handleCreateTraining = async () => {
        try {
            setSubmittingKey('training');
            setError(null);
            await rifleService.createTrainingSession({
                    sessionType: trainingForm.sessionType || null,
                    discipline: trainingForm.discipline || null,
                    focus: trainingForm.focus || null,
                    sessionDate: new Date(trainingForm.sessionDate).toISOString(),
                    durationMinutes: trainingForm.durationMinutes === '' ? 0 : Number(trainingForm.durationMinutes),
                    roundCount: trainingForm.roundCount === '' ? null : Number(trainingForm.roundCount),
                    repCount: trainingForm.repCount === '' ? null : Number(trainingForm.repCount),
                    drills: trainingForm.drills || null,
                    goals: trainingForm.goals || null,
                    observations: trainingForm.observations || null,
                    notes: trainingForm.notes || null,
                    rating: trainingForm.rating === '' ? null : Number(trainingForm.rating),
                    perceivedEffort: trainingForm.perceivedEffort === '' ? null : Number(trainingForm.perceivedEffort),
                    location: trainingForm.location || null
            });
            setTrainingForm(trainingDefaults());
            setShowCreateTraining(false);
            flash('Training session created');
            await fetchTrainingSessions();
        } catch (err) {
            console.error('Error creating training session:', err);
            setError('Failed to create training session.');
        } finally {
            setSubmittingKey(null);
        }
    };

    const closeCreateModal = () => setCreateModalOpen(false);
    const closeEditEventModal = () => setEditEventModalOpen(false);
    const closeEditShootersModal = () => {
        setEditShootersModalOpen(false);
        setShootersRefreshKey((current) => current + 1);
    };
    const closeScoreSheetManagement = () => setScoreSheetManagementOpen(false);
    const openEditEventModal = (event, initialView = 'details') => {
        setSelectedEvent(event);
        setEditEventInitialView(initialView);
        setEditEventModalOpen(true);
    };
    const openEditShootersModal = (event) => {
        setSelectedEvent(event);
        setEditShootersModalOpen(true);
    };
    const openScoreSheetManagement = (event) => {
        setSelectedEvent(event);
        setScoreSheetManagementOpen(true);
    };
    const handleCreateEvent = (newEvent) => {
        setRifleEvents((current) => [newEvent, ...current]);
        closeCreateModal();
    };
    const handleUpdateEvent = async (updatedEvent) => {
        try {
            const updatedEventData = await eventsService.updateEvent(updatedEvent.eventId, updatedEvent);
            setRifleEvents((current) =>
                current.map((event) => event.eventId === updatedEventData.eventId ? updatedEventData : event)
            );
            closeEditEventModal();
        } catch (err) {
            console.error('Error updating rifle event:', err);
            setError('Failed to update rifle event.');
        }
    };
    const handleDeleteEvent = async (eventId) => {
        const event = rifleEvents.find((entry) => entry.eventId === eventId);
        if (!window.confirm(`Delete "${event?.name}"?`)) return;
        try {
            await eventsService.deleteEvent(eventId);
            setRifleEvents((current) => current.filter((entry) => entry.eventId !== eventId));
            flash('Rifle event deleted');
        } catch (err) {
            console.error('Error deleting rifle event:', err);
            setError('Failed to delete rifle event.');
        }
    };
    const handleShootersUpdated = useCallback(() => {
        setShootersRefreshKey((current) => current + 1);
    }, []);

    const renderSummary = (items) => (
        <div className="rifle-summary-grid">
            {items.map((item) => (
                <div key={item.label} className="rifle-summary-card">
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                </div>
            ))}
        </div>
    );

    return (
        <div className="rifle-score-page">
            <div className="rifle-header">
                <div className="rifle-header-content">
                    <div className="rifle-header-icon"><Crosshair size={28} /></div>
                    <div className="rifle-header-text">
                        <h1>Rifle Shooting</h1>
                        <p>Track precision targets, zeros, and training</p>
                    </div>
                </div>
                <button className="rifle-create-btn" onClick={() => setCreateModalOpen(true)}>
                    <Plus size={20} /><span>New Event</span>
                </button>
            </div>

            {error && <div className="rifle-error-message">{error}</div>}
            {activityMessage && <div className="rifle-success-message">{activityMessage}</div>}

            {!isLoading && (
                <section className="rifle-detail-card" style={{ marginBottom: '20px' }}>
                    <div className="rifle-panel-header">
                        <div>
                            <h3 style={{ marginBottom: '4px' }}>Rifle Events</h3>
                            <p className="rifle-muted" style={{ margin: 0 }}>Blue stays reserved for rifle across events, score sheets, and training.</p>
                        </div>
                        <button className="rifle-primary-btn" onClick={() => navigate('/dashboard/score?type=Rifle')}>
                            View All Rifle Events
                        </button>
                    </div>
                    {rifleEvents.length > 0 ? (
                        <div className="rifle-events-grid">
                            {rifleEvents.slice(0, 2).map((event) => (
                                <EventCard
                                    key={event.eventId}
                                    event={{
                                        id: event.eventId,
                                        name: event.name,
                                        eventTime: event.eventTime,
                                        eventDate: event.eventDate,
                                        clubName: event.clubName,
                                        clubId: event.clubId,
                                        eventType: 'Rifle',
                                        ownerName: event.ownerName,
                                        isOwner: event.isOwner,
                                        isShooter: event.isShooter,
                                        imageCount: event.imageCount || 0,
                                        coverImageUrl: event.coverImageUrl || null
                                    }}
                                    onEdit={() => openEditEventModal(event, 'details')}
                                    onEditShooters={() => openEditShootersModal(event)}
                                    onDelete={() => handleDeleteEvent(event.eventId)}
                                    onManageScoreSheets={() => openScoreSheetManagement(event)}
                                    onManageMedia={() => openEditEventModal(event, 'media')}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="rifle-muted" style={{ margin: 0 }}>No rifle events yet. Create one to start building score sheets.</p>
                    )}
                </section>
            )}

            <div className="rifle-tabs">
                <button className={`rifle-tab ${activeTab === 'scoresheets' ? 'active' : ''}`} onClick={() => setActiveTab('scoresheets')}><Award size={18} />Score Sheets</button>
                <button className={`rifle-tab ${activeTab === 'zeros' ? 'active' : ''}`} onClick={() => setActiveTab('zeros')}><Zap size={18} />Zeros</button>
                <button className={`rifle-tab ${activeTab === 'training' ? 'active' : ''}`} onClick={() => setActiveTab('training')}><Crosshair size={18} />Training</button>
            </div>

            {isLoading && <div className="rifle-loading"><div className="rifle-spinner"></div><p>Loading rifle data...</p></div>}

            {!isLoading && activeTab === 'scoresheets' && (
                <div className="rifle-content">
                    {renderSummary([
                        { label: 'Total cards', value: scoreSummary.totalSheets },
                        { label: 'Total shooters', value: scoreSummary.totalShooters },
                        { label: 'Score completion', value: `${scoreSummary.averageCompletion}%` },
                        { label: 'Longest distance', value: `${scoreSummary.longestDistance || 0} m` }
                    ])}
                    <div className="rifle-filter-bar">
                        <input type="text" placeholder="Filter by event, discipline, position, or distance" value={filterText} onChange={(event) => setFilterText(event.target.value)} />
                    </div>
                    {filteredScoreSheets.length > 0 ? <div className="rifle-grid">{filteredScoreSheets.map((sheet) => (
                        <div key={sheet.rifleScoreSheetId} className="rifle-card">
                            <div className="rifle-card-header"><h3>{sheet.eventName}</h3><span className="rifle-discipline">{sheet.disciplineType || 'Rifle'}</span></div>
                            <div className="rifle-card-body">
                                <p><strong>Distance:</strong> {sheet.distance ? `${sheet.distance} ${sheet.distanceUnit || 'metres'}` : 'Not set'}</p>
                                <p><strong>Position:</strong> {sheet.position || 'Not set'}</p>
                                <p><strong>Best score:</strong> {sheet.totalScore ?? 0}/{sheet.possibleScore ?? 0}</p>
                                <p><strong>Shooters:</strong> {sheet.shooterCount || sheet.shooters?.length || 0}</p>
                                <p><strong>Date:</strong> {new Date(sheet.createdAt).toLocaleDateString()}</p>
                            </div>
                            <div className="rifle-card-footer">
                                <button className="rifle-view-btn" onClick={() => navigate(`/dashboard/score/rifle/${sheet.rifleScoreSheetId}`)}><Eye size={16} /> View</button>
                                <button className="rifle-delete-btn" onClick={() => handleDeleteScoreSheet(sheet.rifleScoreSheetId)}><Trash2 size={16} /></button>
                            </div>
                        </div>
                    ))}</div> : <div className="rifle-empty"><Crosshair size={48} /><h3>{scoreSheets.length ? 'No score sheets match that filter' : 'No Score Sheets Yet'}</h3><p>{scoreSheets.length ? 'Try a broader filter.' : 'Create a rifle event to start tracking your precision targets'}</p></div>}
                </div>
            )}

            {!isLoading && activeTab === 'zeros' && (
                <div className="rifle-content">
                    <div className="rifle-content-actions">
                        <button className="rifle-primary-btn" onClick={() => setShowCreateZero((current) => !current)}><Plus size={16} />{showCreateZero ? 'Hide zero form' : 'Log zero'}</button>
                    </div>
                    {showCreateZero && (
                        <section className="rifle-detail-card">
                            <h3>New zero record</h3>
                            <div className="rifle-form-grid">
                                <input type="text" placeholder="Rifle name" value={zeroForm.rifleName} onChange={(event) => setZeroForm((current) => ({ ...current, rifleName: event.target.value }))} />
                                <input type="text" placeholder="Scope name" value={zeroForm.scopeName} onChange={(event) => setZeroForm((current) => ({ ...current, scopeName: event.target.value }))} />
                                <input type="number" min="0" placeholder="Distance" value={zeroForm.distance} onChange={(event) => setZeroForm((current) => ({ ...current, distance: event.target.value }))} />
                                <input type="text" placeholder="Distance unit" value={zeroForm.distanceUnit} onChange={(event) => setZeroForm((current) => ({ ...current, distanceUnit: event.target.value }))} />
                                <input type="number" step="0.1" placeholder="Clicks elevation" value={zeroForm.clicksElevation} onChange={(event) => setZeroForm((current) => ({ ...current, clicksElevation: event.target.value }))} />
                                <input type="number" step="0.1" placeholder="Clicks windage" value={zeroForm.clicksWindage} onChange={(event) => setZeroForm((current) => ({ ...current, clicksWindage: event.target.value }))} />
                                <input type="text" placeholder="Ammunition" value={zeroForm.ammunition} onChange={(event) => setZeroForm((current) => ({ ...current, ammunition: event.target.value }))} />
                                <input type="number" min="0" placeholder="Confirmation shots" value={zeroForm.confirmationShots} onChange={(event) => setZeroForm((current) => ({ ...current, confirmationShots: event.target.value }))} />
                                <input type="number" step="0.01" placeholder="Group size" value={zeroForm.groupSize} onChange={(event) => setZeroForm((current) => ({ ...current, groupSize: event.target.value }))} />
                                <input type="text" placeholder="Group size unit" value={zeroForm.groupSizeUnit} onChange={(event) => setZeroForm((current) => ({ ...current, groupSizeUnit: event.target.value }))} />
                                <input type="number" step="0.01" placeholder="Vertical offset" value={zeroForm.verticalOffset} onChange={(event) => setZeroForm((current) => ({ ...current, verticalOffset: event.target.value }))} />
                                <input type="number" step="0.01" placeholder="Horizontal offset" value={zeroForm.horizontalOffset} onChange={(event) => setZeroForm((current) => ({ ...current, horizontalOffset: event.target.value }))} />
                                <input type="text" placeholder="Offset unit" value={zeroForm.offsetUnit} onChange={(event) => setZeroForm((current) => ({ ...current, offsetUnit: event.target.value }))} />
                                <input type="date" value={zeroForm.zeroDate} onChange={(event) => setZeroForm((current) => ({ ...current, zeroDate: event.target.value }))} />
                            </div>
                            <div className="rifle-form-group"><label>Confirmation notes</label><textarea value={zeroForm.confirmationNotes} onChange={(event) => setZeroForm((current) => ({ ...current, confirmationNotes: event.target.value }))} /></div>
                            <div className="rifle-form-group"><label>Notes</label><textarea value={zeroForm.notes} onChange={(event) => setZeroForm((current) => ({ ...current, notes: event.target.value }))} /></div>
                            <div className="rifle-form-actions">
                                <label className="rifle-checkbox-row"><input type="checkbox" checked={zeroForm.zeroConfirmed} onChange={(event) => setZeroForm((current) => ({ ...current, zeroConfirmed: event.target.checked }))} />Zero confirmed</label>
                                <label className="rifle-checkbox-row"><input type="checkbox" checked={zeroForm.isBaseZero} onChange={(event) => setZeroForm((current) => ({ ...current, isBaseZero: event.target.checked }))} />Base zero</label>
                                <button className="rifle-primary-btn" onClick={handleCreateZero} disabled={submittingKey === 'zero'}>{submittingKey === 'zero' ? 'Saving...' : 'Save zero'}</button>
                            </div>
                        </section>
                    )}
                    {renderSummary([
                        { label: 'Total zeros', value: zeroSummary.totalZeros },
                        { label: 'Base zeros', value: zeroSummary.baseZeros },
                        { label: 'Confirmed zeros', value: zeroSummary.confirmedZeros },
                        { label: 'Tracked rifles', value: zeroSummary.uniqueRifles },
                        { label: 'Furthest zero', value: `${zeroSummary.maxDistance || 0} m` }
                    ])}
                    <div className="rifle-filter-bar">
                        <input type="text" placeholder="Filter by rifle, scope, ammunition, or distance" value={filterText} onChange={(event) => setFilterText(event.target.value)} />
                    </div>
                    {filteredZeros.length > 0 ? <div className="rifle-grid">{filteredZeros.map((zero) => (
                        <div key={zero.rifleZeroId} className="rifle-card">
                            <div className="rifle-card-header"><h3>{zero.equipmentName || zero.rifleName || 'Unknown'}</h3><span className="rifle-calibre">{zero.distance} {zero.distanceUnit || 'metres'}</span></div>
                            <div className="rifle-card-body">
                                <p><strong>Distance:</strong> {zero.distance} {zero.distanceUnit || 'metres'}</p>
                                <p><strong>Validation:</strong> {zero.zeroConfirmed ? 'Confirmed' : 'Needs confirmation'}</p>
                                <p><strong>Group:</strong> {zero.groupSize ? `${zero.groupSize} ${zero.groupSizeUnit || ''}` : 'Not recorded'}</p>
                                <p><strong>Elevation:</strong> {zero.clicksElevation ?? 0} {zero.elevationDirection || ''}</p>
                                <p><strong>Windage:</strong> {zero.clicksWindage ?? 0} {zero.windageDirection || ''}</p>
                                <p><strong>Scope:</strong> {zero.scopeName || 'Not set'}</p>
                                <p><strong>Date:</strong> {new Date(zero.zeroDate || zero.createdAt).toLocaleDateString()}</p>
                            </div>
                            <div className="rifle-card-footer">
                                <button className="rifle-view-btn" onClick={() => navigate(`/dashboard/score/zero/${zero.rifleZeroId}`)}><Eye size={16} /> View</button>
                                <button className="rifle-delete-btn" onClick={() => handleDeleteZero(zero.rifleZeroId)}><Trash2 size={16} /></button>
                            </div>
                        </div>
                    ))}</div> : <div className="rifle-empty"><Zap size={48} /><h3>{zeros.length ? 'No zero records match that filter' : 'No Zeros Recorded'}</h3><p>{zeros.length ? 'Try a broader filter.' : 'Log your rifle zeros to track scope adjustments and ballistic data'}</p></div>}
                </div>
            )}

            {!isLoading && activeTab === 'training' && (
                <div className="rifle-content">
                    <div className="rifle-content-actions">
                        <button className="rifle-primary-btn" onClick={() => setShowCreateTraining((current) => !current)}><Plus size={16} />{showCreateTraining ? 'Hide training form' : 'Log training'}</button>
                    </div>
                    {showCreateTraining && (
                        <section className="rifle-detail-card">
                            <h3>New training session</h3>
                            <div className="rifle-form-grid">
                                <input type="text" placeholder="Session type" value={trainingForm.sessionType} onChange={(event) => setTrainingForm((current) => ({ ...current, sessionType: event.target.value }))} />
                                <input type="text" placeholder="Discipline" value={trainingForm.discipline} onChange={(event) => setTrainingForm((current) => ({ ...current, discipline: event.target.value }))} />
                                <input type="text" placeholder="Focus" value={trainingForm.focus} onChange={(event) => setTrainingForm((current) => ({ ...current, focus: event.target.value }))} />
                                <input type="date" value={trainingForm.sessionDate} onChange={(event) => setTrainingForm((current) => ({ ...current, sessionDate: event.target.value }))} />
                                <input type="number" min="0" placeholder="Duration minutes" value={trainingForm.durationMinutes} onChange={(event) => setTrainingForm((current) => ({ ...current, durationMinutes: event.target.value }))} />
                                <input type="number" min="0" placeholder="Rounds" value={trainingForm.roundCount} onChange={(event) => setTrainingForm((current) => ({ ...current, roundCount: event.target.value }))} />
                                <input type="number" min="0" placeholder="Reps" value={trainingForm.repCount} onChange={(event) => setTrainingForm((current) => ({ ...current, repCount: event.target.value }))} />
                                <input type="text" placeholder="Location" value={trainingForm.location} onChange={(event) => setTrainingForm((current) => ({ ...current, location: event.target.value }))} />
                            </div>
                            <div className="rifle-form-group"><label>Notes</label><textarea value={trainingForm.notes} onChange={(event) => setTrainingForm((current) => ({ ...current, notes: event.target.value }))} /></div>
                            <div className="rifle-form-actions">
                                <button className="rifle-primary-btn" onClick={handleCreateTraining} disabled={submittingKey === 'training'}>{submittingKey === 'training' ? 'Saving...' : 'Save training'}</button>
                            </div>
                        </section>
                    )}
                    {renderSummary([
                        { label: 'Total sessions', value: trainingStats?.totalSessions ?? trainingSessions.length },
                        { label: 'Total minutes', value: trainingStats?.totalMinutes ?? 0 },
                        { label: 'Sessions this month', value: trainingStats?.sessionsThisMonth ?? 0 },
                        { label: 'Top discipline', value: trainingStats?.mostPracticedDiscipline || 'N/A' }
                    ])}
                    <div className="rifle-filter-bar">
                        <input type="text" placeholder="Filter by session type, discipline, focus, or location" value={filterText} onChange={(event) => setFilterText(event.target.value)} />
                    </div>
                    {filteredTrainingSessions.length > 0 ? <div className="rifle-grid">{filteredTrainingSessions.map((session) => (
                        <div key={session.trainingSessionId} className="rifle-card">
                            <div className="rifle-card-header"><h3>{session.focus || session.discipline || 'Training session'}</h3><span className="rifle-type">{session.sessionType || 'Training'}</span></div>
                            <div className="rifle-card-body">
                                <p><strong>Equipment:</strong> {session.equipmentName || 'Not set'}</p>
                                <p><strong>Duration:</strong> {session.durationMinutes} min</p>
                                <p><strong>Focus:</strong> {session.focus || session.discipline || 'General practice'}</p>
                                <p><strong>Notes:</strong> {session.notes ? `${session.notes.substring(0, 50)}${session.notes.length > 50 ? '...' : ''}` : 'None'}</p>
                                <p><strong>Date:</strong> {new Date(session.sessionDate || session.createdAt).toLocaleDateString()}</p>
                            </div>
                            <div className="rifle-card-footer">
                                <button className="rifle-view-btn" onClick={() => navigate(`/dashboard/score/training/${session.trainingSessionId}`)}><Eye size={16} /> View</button>
                                <button className="rifle-delete-btn" onClick={() => handleDeleteTraining(session.trainingSessionId)}><Trash2 size={16} /></button>
                            </div>
                        </div>
                    ))}</div> : <div className="rifle-empty"><Crosshair size={48} /><h3>{trainingSessions.length ? 'No training sessions match that filter' : 'No Training Sessions'}</h3><p>{trainingSessions.length ? 'Try a broader filter.' : 'Log your dry fire and live fire training sessions'}</p></div>}
                </div>
            )}

            <CreateEvent
                isOpen={createModalOpen}
                closeModal={closeCreateModal}
                onEventCreated={handleCreateEvent}
                initialEventType="Rifle"
            />
            <EditEvent
                isOpen={editEventModalOpen}
                closeModal={closeEditEventModal}
                event={selectedEvent}
                onEventUpdated={handleUpdateEvent}
                initialView={editEventInitialView}
                canEditDetails={selectedEvent?.isOwner !== false}
                canManageMedia={selectedEvent?.isOwner !== false}
            />
            <EditShooters
                isOpen={editShootersModalOpen}
                closeModal={closeEditShootersModal}
                event={selectedEvent}
                onShootersUpdated={handleShootersUpdated}
            />
            <ScoreSheetManagement
                eventId={selectedEvent?.eventId}
                isOpen={scoreSheetManagementOpen}
                onClose={closeScoreSheetManagement}
                refreshKey={shootersRefreshKey}
                canManage={selectedEvent?.isOwner !== false}
            />
        </div>
    );
};

export default RifleScore;
